import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { ZoneKonva } from "@project/floorplanner/konva/zones/zone-konva";
import Konva from "konva";

export class DangerAreaKonva {
  static init(dangerArea: DangerArea): Konva.Group {
    const parentGroup = new Konva.Group();

    const dangerAreaGroup = new Konva.Group({
      x: dangerArea.x,
      y: dangerArea.y,
      height: dangerArea.height,
      width: dangerArea.width,
      rotation: dangerArea.rotation,
      draggable: false,
    });
    dangerAreaGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, dangerArea);

    const color = "#700000";
    const strokeWidth = 4;
    const rect = new Konva.Rect({
      x: 0,
      y: 0,
      height: dangerArea.height,
      width: dangerArea.width,
      fill: color + "1A",
      lineJoin: "bevel",
      strokeEnabled: false,
      dash: [10, 2],
    });
    const borderRect = new Konva.Rect({
      x: 0,
      y: 0,
      width: dangerArea.width,
      height: dangerArea.height,
      stroke: color,
      strokeWidth,
    });
    dangerAreaGroup.add(rect);
    dangerAreaGroup.add(borderRect);

    const itemText = FloorplanItemTextKonva.init(dangerArea);
    const text = FloorplanItemTextKonva.getText(itemText);
    ZoneKonva.setTextPosition(itemText, dangerAreaGroup);
    ZoneKonva.scaleText(text, dangerAreaGroup);

    parentGroup.add(dangerAreaGroup, itemText);
    let lines = this.addDangerAreaHatch(dangerAreaGroup, rect.width(), rect.height());

    dangerAreaGroup.on("transformstart", () => {
      itemText.hide();
    });

    dangerAreaGroup.on("transform", () => {
      lines.forEach((shape) => shape.destroy());

      dangerAreaGroup.width(Math.max(5, dangerAreaGroup.width() * dangerAreaGroup.scaleX()));
      dangerAreaGroup.height(Math.max(5, dangerAreaGroup.height() * dangerAreaGroup.scaleY()));

      rect.width(dangerAreaGroup.width());
      rect.height(dangerAreaGroup.height());

      borderRect.width(dangerAreaGroup.width());
      borderRect.height(dangerAreaGroup.height());

      dangerAreaGroup.scaleX(1);
      dangerAreaGroup.scaleY(1);

      lines = this.addDangerAreaHatch(dangerAreaGroup, dangerAreaGroup.width(), dangerAreaGroup.height());
    });
    dangerAreaGroup.on("transformend", () => {
      dangerArea.updateForm(
        dangerAreaGroup.width() * dangerAreaGroup.scaleX(),
        dangerAreaGroup.height() * dangerAreaGroup.scaleY(),
        dangerAreaGroup.rotation(),
        dangerAreaGroup.x(),
        dangerAreaGroup.y(),
      );
      ZoneKonva.scaleText(text, dangerAreaGroup);
      ZoneKonva.setTextPosition(itemText, dangerAreaGroup);
      const labelHidden = itemText.getAttr("labelHidden") === true;
      if (!labelHidden) {
        itemText.show();
      }
    });

    dangerAreaGroup.on("dragmove", () => {
      ZoneKonva.setTextPosition(itemText, dangerAreaGroup);
    });

    return parentGroup;
  }

  static refreshSize(zone: DangerArea, zoneGroup: Konva.Group, width: number, height: number) {
    const subGroup = ZoneKonva.resizeZoneGroup(zoneGroup, width, height);

    const lines = this.getHatchFromGroup(subGroup);
    lines.forEach((shape) => shape.destroy());
    this.addDangerAreaHatch(subGroup, zoneGroup.width(), zoneGroup.height());

    this.refreshItemText(zone, zoneGroup);
  }

  static refreshItemText(dangerArea: DangerArea, group: Konva.Group) {
    const text = group.getChildren()[1] as Konva.Group;
    const dangerAreaGroup = group.getChildren()[0] as Konva.Group;
    dangerAreaGroup.width(dangerArea.width);
    dangerAreaGroup.height(dangerArea.height);

    FloorplanItemTextKonva.refreshText(dangerArea, text);
    const textNode = FloorplanItemTextKonva.getText(text);
    ZoneKonva.scaleText(textNode, dangerAreaGroup);
    ZoneKonva.setTextPosition(text, dangerAreaGroup);
  }

  private static addDangerAreaHatch(group: Konva.Group, width: number, height: number): Konva.Line[] {
    const lines: Konva.Line[] = [];
    for (let i = 10, j = 10; i <= width + height; i += 40, j += 40) {
      const line = new Konva.Line({
        stroke: "#7000004D",
        strokeWidth: 4,
      });
      if (i >= height && j >= width) {
        line.points([i - height, height, width, j - width]);
      } else if (i >= height) {
        line.points([i - height, height, j, 0]);
      } else if (j >= width) {
        line.points([0, i, width, j - width]);
      } else {
        line.points([0, i, j, 0]);
      }
      lines.push(line);
      group.add(line);
    }
    return lines;
  }

  private static getHatchFromGroup(group: Konva.Group) {
    return group.getChildren().filter((child): child is Konva.Line => child.getClassName() === "Line");
  }
}
